/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useEffect, useState } from "react";
import { BackgroundPanel, TableAdvanced, ImageButton } from "vr-react-components";
import { Button } from "vr-react-components";
import { useDispatch, useSelector } from "react-redux";
import { createRoom, deleteRoom, makeAll } from "./SocketConnection";
import { Text } from "vr-react-components";
import { createButtonState } from "../constants/ButtonConstants";
import MicrosoftTeamsPanel from "./microsoft_teams/MicrosoftTeamsPanel";
import { setCooperative, setNotification } from "../store/slice/privateSlice";
import { updateRoomData } from "../store/slice/roomSlice";
import { handleLogout } from "../handlers/RoomHandlers";
import * as webRTCHandler from "../handlers/webRTCHandler";
import { sendMicState } from "./SocketConnection"
import { CONTRIBUTOR, MIC_DISABLED, MIC_ENABLED, OWNER, VIEWER } from "../store/constants/sharedModeFields";

const SharePanel = ({ width, height, roomId, ...otherAttributes }) => {
    const configuredStunServer = useSelector((state) => state.room.configuredStunServer);
    const roomCreatorSocketId = useSelector((state) => state.room.roomCreator?.socketId);
    const currentUserData = useSelector((state) => state.user.data);
    const audioOnly = useSelector((state) => state.room.audioOnly);
    const participants = useSelector((state) => state.room.participants);
    const device = useSelector((state) => state.private.device);
    const cooperative = useSelector((state) => state.private.device.cooperative);
    const localStream = useSelector((state) => state.room.localStream)
    const isOwner = roomCreatorSocketId === currentUserData.socketId
    // shareButton
    const shareButtonInit = createButtonState("Activate", Button.constants.primary)
    const shareButtonActive = createButtonState(isOwner ? "End" : "Leave", Button.constants.danger)
    // onlyViewCopyButton & onlyViewTeamsButton
    const copyLinkButtonInit = createButtonState("Copy Link", Button.constants.primary)
    const CopyLinkButtonClicked = createButtonState("Link Copied", Button.constants.success)
    // teams gruop button have not multiple state
    const TeamsGroupButton = createButtonState("Teams Group", Button.constants.primary)

    const [shareButton, setShareButton] = useState(!roomId ? shareButtonInit : shareButtonActive);
    const [onlyViewCopyButton, setOnlyViewCopyButton] = useState(copyLinkButtonInit);
    const [contributorCopyButton, setContributorCopyButton] = useState(copyLinkButtonInit);
    const [page, setPage] = useState({ page: device.canModify ? "default" : "participants" });
    const [mute, setMute] = useState(true)

    useEffect(() => {
        setShareButton(!roomId ? createButtonState("Activate", Button.constants.primary) : createButtonState(isOwner ? "End" : "Leave", Button.constants.danger))
    }, [roomId, isOwner])

    useEffect(() => {
        setPage({ page: device.canModify ? "default" : "participants" })
    }, [device.canModify])
    
    useEffect(() => {
        setMute(participants.find((participant) => participant.socketId === currentUserData.socketId)?.muted === MIC_ENABLED)
    }, [participants, currentUserData])

    const dispatch = useDispatch();

    const createRoomSetUp = (newActiveRoom) => {
        dispatch(updateRoomData(newActiveRoom))
        dispatch(setCooperative(true));
        setShareButton(shareButtonActive)
    }

    const handleToggleSharingMode = () => {
        if (!roomId) {
            const owner = {
                ...currentUserData,
                role: OWNER,
                muted: MIC_ENABLED
            }

            if (configuredStunServer)
                webRTCHandler.getLocalStreamPreview(audioOnly, () => {
                    createRoom(owner, createRoomSetUp);
                });
            else
                createRoom(owner, createRoomSetUp);

            dispatch(setNotification("You activated the sharing mode"))
        } else {
            if (isOwner)
                deleteRoom(roomId)
            else
                handleLogout()

            if (configuredStunServer)
                webRTCHandler.closeAllWebRTCConnections()
            setMute(true)
            setShareButton(shareButtonInit)
            dispatch(setNotification("You ended the sharing mode"))
            localStream.getAudioTracks()[0].enabled = true
        }
    }

    const handleCopyLinkViewer = () => {
        setTimeout(() => {
            setOnlyViewCopyButton(copyLinkButtonInit);
        }, 2000)

        setOnlyViewCopyButton(CopyLinkButtonClicked);

        navigator.clipboard.writeText(
            `${window.location.origin}/cooperative/login/${window.btoa(
                `${roomId}&&` + VIEWER,
            )}`,
        );
    }

    const handleCopyLinkParticipant = () => {
        setTimeout(() => {
            setContributorCopyButton(copyLinkButtonInit);
        }, 2000)

        setContributorCopyButton(CopyLinkButtonClicked);

        navigator.clipboard.writeText(
            `${window.location.origin}/cooperative/login/${window.btoa(
                `${roomId}&&` + CONTRIBUTOR,
            )}`,
        );
    }

    const participantsList = [
        ['Username', 'Role'],
        ...participants.map((p) => { return [p.username, p.role, p.muted] }
        ),
    ]

    const handleToggleMute = () => {
        const audioTracks = localStream.getAudioTracks()[0]

        audioTracks.enabled = !audioTracks.enabled

        sendMicState(audioTracks.enabled ? MIC_ENABLED : MIC_DISABLED)

        setMute(old => !old)
    }

    return (
        <BackgroundPanel
            width={width}
            height={page.page === "teams" ? height * 1.5 : height}
            {...otherAttributes}
        >
            {page.page !== "teams" &&
                <a-entity
                    position={`${-width / 2 + width * 0.05} ${height / 2 - height / 10} ${height / 100
                        }`}
                >
                    <Button
                        text={shareButton.text}
                        type={shareButton.type}
                        onClickHandler={handleToggleSharingMode}
                        aframeClasses={"collidable"}
                        width={width / 5}
                        height={height / 7}
                        radius={width / 30}
                        position={`${width / 3.5 + width / 2} -0.01 0`}
                        textWidth={width / 5}
                        textSizeDecimal={40 / width}
                    />
                    {cooperative &&
                        <ImageButton
                            onClickHandler={handleToggleMute}
                            aframeClasses={"collidable"}
                            hoverColor={"#151515"}
                            width={width / 12}
                            height={height / 5}
                            position={`${width / 20 + width / 2} -0.01 0`}
                            textWidth={width / 5}
                            textSizeDecimal={40 / width}
                            src={mute ? MIC_ENABLED : MIC_DISABLED}
                            disabled={!localStream}
                        />
                    }
                </a-entity>
            }
            {page.page === "default" && (
                <a-entity
                    position={`${-width / 2 + width * 0.05} ${height / 2 - height / 10} ${height / 100
                        }`}
                >
                    <Text
                        width={width / 2}
                        sizeDecimal={30 / width}
                        text="Sharing mode"
                    />
                    <a-plane
                        width={width * 0.9}
                        height={height * 0.3}
                        color={"#464646"}
                        position={`${width * 0.45} ${-height * 0.2 - height * 0.15} 0`}
                    >
                        <a-plane
                            width={width * 0.89}
                            height={height * 0.15 - width * 0.01}
                            color={"#121212"}
                            position={`0 ${height * 0.075} ${width / 500}`}
                        >
                            <Text
                                width={width / 2}
                                sizeDecimal={40 / width}
                                text="Viewer Access"
                                position={`${-width * 0.425} 0 ${height / 100}`}
                            />
                            <Button
                                text={onlyViewCopyButton.text}
                                type={onlyViewCopyButton.type}
                                disabled={!Boolean(roomId)}
                                aframeClasses={device.canModify ? "collidable" : ""}
                                position={`${width * 0.45 - width * 0.36} 0 ${width / 200}`}
                                width={width * 0.22}
                                height={height * 0.1}
                                textSizeDecimal={55 / width}
                                onClickHandler={handleCopyLinkViewer}
                            />
                            <Button
                                text={TeamsGroupButton.text}
                                type={TeamsGroupButton.type}
                                disabled={!Boolean(roomId)}
                                textSizeDecimal={55 / width}
                                width={width * 0.22}
                                height={height * 0.1}
                                aframeClasses={device.canModify ? "collidable" : ""}
                                position={`${width * 0.45 - width * 0.13} 0 ${width / 200}`}
                                onClickHandler={() => {
                                    setPage({
                                        page: "teams",
                                        data: btoa(`${roomId}&&` + VIEWER),
                                    });
                                }}
                            />
                        </a-plane>
                        <a-plane
                            width={width * 0.89}
                            height={height * 0.15 - width * 0.01}
                            color="#121212"
                            position={`0 ${-height * 0.075} ${width / 500}`}
                        >
                            <Text
                                width={width / 2}
                                sizeDecimal={40 / width}
                                text="Contributor Access"
                                position={`${-width * 0.425} 0 ${height / 100}`}
                            />
                            <Button
                                text={contributorCopyButton.text}
                                type={contributorCopyButton.type}
                                disabled={!Boolean(roomId)}
                                aframeClasses={device.canModify ? "collidable" : ""}
                                position={`${width * 0.45 - width * 0.36} 0 ${width / 200
                                    }`}
                                width={width * 0.22}
                                height={height * 0.1}
                                textSizeDecimal={55 / width}
                                onClickHandler={handleCopyLinkParticipant}
                            />
                            <Button
                                text={TeamsGroupButton.text}
                                type={TeamsGroupButton.type}
                                disabled={!Boolean(roomId)}
                                textSizeDecimal={55 / width}
                                width={width * 0.22}
                                height={height * 0.1}
                                aframeClasses={device.canModify ? "collidable" : ""}
                                position={`${width * 0.45 - width * 0.13} 0 ${width / 200
                                    }`}
                                onClickHandler={() => {
                                    setPage({
                                        page: "teams",
                                        data: btoa(`${roomId}&&` + CONTRIBUTOR),
                                    });
                                }}
                            />
                        </a-plane>
                    </a-plane>
                    <Text
                        width={width}
                        sizeDecimal={42.5 / width}
                        text={"Participants: " + participants.length}
                        position={`0.01 ${-height * 0.68} 0`}
                    />
                    <Button
                        text={"See Participants"}
                        type={Button.constants.primary}
                        disabled={!Boolean(roomId)}
                        textSizeDecimal={50 / width}
                        width={width * 0.3}
                        height={height * 0.13}
                        aframeClasses={"collidable"}
                        position={!device.inVRMode ?
                            `${width * 0.86 - width * 0.13} -0.3 ${width / 200}` :
                            `${width * 0.86 - width * 0.13} -0.81 ${width / 200}`
                        }
                        onClickHandler={() => {
                            setPage({
                                page: "participants"
                            });
                        }}
                    />
                </a-entity>
            )}
            {page.page === "teams" && (
                <MicrosoftTeamsPanel
                    width={width}
                    height={height}
                    onClose={() => setPage({ page: "default" })}
                    data={page.data}
                    aframeClasses={device.canModify ? "collidable" : ""}
                />
            )}
            {page.page === "participants" && (
                <a-entity
                    position={`${-width / 2 + width * 0.05} ${height / 2 - height / 10} ${height / 100
                        }`}
                >
                    <Text
                        width={width}
                        sizeDecimal={50 / width}
                        text={'[ ' + currentUserData.username + ' ] Sharing Session'}
                        position={`0 0 0`}
                    />
                    <Text
                        width={width}
                        sizeDecimal={50 / width}
                        text={"Participants: " + participants.length}
                        position={`0 ${-height * 0.12} 0`}
                    />
                    <TableAdvanced
                        micStates={participants.map((item) => item.muted)}
                        width={width * 0.9}
                        height={height * 0.5}
                        inVRMode={device.inVRMode}
                        textSizeDecimal={50 / width}
                        backgroundColor={"#000000"}
                        headerColor={"#121212"}
                        aframeClasses={"collidable"}
                        disabled={true}
                        position={!device.inVRMode ?
                            `0.36 -0.07 ${width / 100}` : `0.86 -0.2 ${width / 100}`
                        }
                        data={participantsList}
                    />
                    {device.canModify &&
                        <Button
                            text="Back"
                            width={width * 0.2}
                            height={height * 0.13}
                            type={Button.constants.dark}
                            textSizeDecimal={40 / width}
                            position={`${width * 0.087} ${-height * 0.75} 0`}
                            aframeClasses={"collidable"}
                            onClickHandler={() => {
                                setPage({
                                    page: "default"
                                });
                            }}
                        />
                    }
                    {isOwner ?
                        <Button
                            text={"All Viewers"}
                            type={Button.constants.primary}
                            textSizeDecimal={50 / width}
                            width={width * 0.3}
                            height={height * 0.13}
                            aframeClasses={"collidable"}
                            position={!device.inVRMode ?
                                `${width * 0.42} ${-height * 0.75} 0` :
                                `${width * 0.737} ${-height * 0.75} 0`
                            }
                            onClickHandler={() => {
                                makeAll(VIEWER, roomId)
                            }}
                            disabled={participants.length < (isOwner ? 2 : 3)}
                        /> : null
                    }
                    {isOwner ?
                        <Button
                            text={"All Contributor"}
                            type={Button.constants.primary}
                            textSizeDecimal={50 / width}
                            width={width * 0.3}
                            height={height * 0.13}
                            aframeClasses={"collidable"}
                            position={!device.inVRMode ?
                                `${width * 0.74} ${-height * 0.75} 0` :
                                `${width * 0.41} ${-height * 0.75} 0`
                            }
                            onClickHandler={() => {
                                makeAll(CONTRIBUTOR, roomId)
                            }}
                            disabled={participants.length < (isOwner ? 2 : 3)}
                        /> : null
                    }
                </a-entity>
            )}
        </BackgroundPanel>
    );
};

export default SharePanel;
